/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RuleSetWorkflowPage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */


import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import RuleSetStepperDrawer from "./RuleSetStepper/RuleSetStepperDrawer";
import StepBasicDetails from "./RuleSetStepper/StepBasicDetails";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { HandleApiActions } from "../../../redux/actions";
import ruleEngineActions from "../../../redux/actions/ruleEngineAction";
import { normalizeRuleSetCode } from "../../../utils/normalizeRuleSetCode";
import StepLegalScope from "./RuleSetStepper/StepLegalScope";
import RuleWorkflowBuilder from "./RuleWorkflowBuilder";
import WorkflowPortal from "./RuleSetStepper/WorkflowPortal";
import { ReactFlowProvider } from "reactflow";

function RuleSetWorkflowPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const rawRuleSetId = useParams().ruleSetId;

  const normalizedRuleSetId =
    rawRuleSetId && rawRuleSetId !== "null" && rawRuleSetId !== "undefined"
      ? rawRuleSetId
      : null;

  const workflowRef = useRef(null);

  const workflowHostRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [currentRuleSetId, setCurrentRuleSetId] = useState(null);
  const [workflowFullscreen, setWorkflowFullscreen] = useState(false);

  const [workflowLoaded, setWorkflowLoaded] = useState(false);
  const [workflowMeta, setWorkflowMeta] = useState(null);
  const [initialWorkflowId, setInitialWorkflowId] = useState(null);

  const [stepData, setStepData] = useState({
    basic: {
      name: "",
      description: "",
    },
    legal: {},
    workflow: {},
  });

  const [stepErrors, setStepErrors] = useState({
    basic: {},
    legal: {},
    workflow: {},
  });

  const loadRuleSet = async () => {
    const api = {
      ...ruleEngineActions.view_rule_set_with_id,
      api_name: ruleEngineActions.view_rule_set_with_id.api_name.replace(
        ":ruleSetId",
        currentRuleSetId,
      ),
    };

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...api,
        }),
      ),
    );

    if (resp?.data) {
      const rs = resp.data;

      setStepData((prev) => ({
        ...prev,
        basic: {
          name: rs.name || "",
          description: rs.description || "",
        },
        legal: rs.legal_scope || {},
        workflow: rs.workflow || {},
      }));
    }
  };

  const loadWorkflow = async () => {
    const api = {
      ...ruleEngineActions.get_workflow_for_rule_set,
      api_name: ruleEngineActions.get_workflow_for_rule_set.api_name.replace(
        ":ruleSetId",
        currentRuleSetId,
      ),
    };

    const resp = await Promise.resolve(dispatch(HandleApiActions(api)));

    if (resp?.data?.data?.workflowId) {
      setInitialWorkflowId(resp?.data?.data?.workflowId);
    }

    if (resp?.data?.data?.definition) {
      setWorkflowMeta(resp?.data?.data?.definition);
      setWorkflowLoaded(true);
    }
  };

  useEffect(() => {
    if (normalizedRuleSetId) {
      setCurrentRuleSetId(normalizedRuleSetId);
    }
  }, [normalizedRuleSetId]);

  useEffect(() => {
    if (!currentRuleSetId) return;

    loadRuleSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRuleSetId]);

  useEffect(() => {
    if (activeStep !== 2) return; // Only Step 3
    if (!currentRuleSetId) return; // Must have RuleSet
    if (workflowLoaded) return; // Prevent reload
    loadWorkflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, currentRuleSetId]);


  const createRuleSetWorkflowAssociation = async (workflowId, ruleSetId) => {
    let response;
    try {
      const api = {
        ...ruleEngineActions.atatch_workflow_to_rule_set,
        api_name:
          ruleEngineActions.atatch_workflow_to_rule_set.api_name.replace(
            ":ruleSetId",
            ruleSetId,
          ),
      };
      response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...api,
            params: {
              workflowId: workflowId,
            },
          }),
        ),
      );
    } catch (error) {
      response = {
        error: true,
        errorMessage: error,
      };
    }
    return response;
  };

  const handleStepBasic = async () => {
    const { name, description } = stepData.basic;

    if (!name) {
      setStepErrors((prev) => ({
        ...prev,
        basic: { name: "Name is required" },
      }));
      return;
    }

    if (name?.length < 4) {
      setStepErrors((prev) => ({
        ...prev,
        basic: {
          name: "A Rule set name should be atleast 3 characters long",
        },
      }));
      return;
    }

    const api =
      currentRuleSetId == null
        ? ruleEngineActions.add_rule_engine
        : {
          ...ruleEngineActions.update_rule_engine,
          api_name: ruleEngineActions.update_rule_engine.api_name.replace(
            ":ruleSetId",
            currentRuleSetId,
          ),
        };

    const normalizedCode = normalizeRuleSetCode(name);

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...api,
          params: {
            rule_set_id: currentRuleSetId,
            name,
            description,
            code: normalizedCode,
          },
          show_toast: true,
        }),
      ),
    );

    if (resp.error) {
      setStepErrors((prev) => ({
        ...prev,
        basic: resp.errors || { _form: resp.msg },
      }));
      return;
    }

    setStepErrors((prev) => ({ ...prev, basic: {} }));
    setCurrentRuleSetId(resp.data.rule_set_id);
    setActiveStep(1);
  };

  const handleStepLegalScope = () => {
    setActiveStep(2);
  };

  const handleStepWorkflow = async () => {
    if (!workflowRef.current) return;

    const result = await workflowRef.current.saveWorkflow();

    if (!result?.success || !result?.data?.id) return;

    if (initialWorkflowId) {
      navigate("/dashboard/rule-center");
      return;
    }

    const workflowId = result?.data?.id;

    const ruleSetAssociation = await createRuleSetWorkflowAssociation(
      workflowId,
      currentRuleSetId,
    );

    if (!ruleSetAssociation.error) {
      navigate("/dashboard/rule-center");
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) return handleStepBasic();
    if (activeStep === 1) return handleStepLegalScope();
    if (activeStep === 2) return handleStepWorkflow();
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  return (
    <>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Legal Requirements", path: "/dashboard/rule-center" },
              { label: currentRuleSetId ? "Edit Rule" : "Create Rule" },
            ]}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flex: 1, //  consume remaining space
            minHeight: 0,
          }}
        >
          {!isMobile && <RuleSetStepperDrawer activeStep={activeStep} />}
          {isMobile && (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Step {activeStep + 1} of 3
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                px: isMobile ? 2 : 4, // horizontal padding instead of centering
                pt: 3,
                minHeight: 0, //  allow shrinking
                flex: 1, // consume remaining space
                width: "100%",
                overflow: "hidden", // stop page scroll
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  height: "100%",
                  maxHeight: {
                    xs: "calc(100vh - 220px)",
                    sm: "calc(100vh - 240px)",
                    md: "calc(100vh - 260px)",
                  },
                  overflowY: "auto", //  Paper scrolls
                  overflowX: "hidden",
                  p: 3,
                  borderRadius: 2,
                  minHeight: 0,
                }}
              >
                {activeStep === 0 && (
                  <StepBasicDetails
                    value={stepData.basic}
                    errors={stepErrors.basic}
                    onChange={(data) =>
                      setStepData((prev) => ({
                        ...prev,
                        basic: data,
                      }))
                    }
                  />
                )}

                {activeStep === 1 && (
                  <StepLegalScope
                    ruleSetId={currentRuleSetId}
                    value={stepData.legal}
                    onChange={(data) =>
                      setStepData((prev) => ({
                        ...prev,
                        legal: data,
                      }))
                    }
                  />
                )}

                <Box
                  ref={workflowHostRef}
                  sx={{
                    position: workflowFullscreen ? "fixed" : "relative",
                    inset: workflowFullscreen ? 0 : "auto",
                    width: workflowFullscreen ? "100vw" : "100%",
                    height: workflowFullscreen ? "100vh" : "100%",
                    zIndex: workflowFullscreen ? 1200 : "auto",
                    backgroundColor: "#fff",
                    display:
                      activeStep === 2 || workflowFullscreen ? "block" : "none",
                  }}
                />
              </Paper>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                px: 4,
                pt: 1,
                paddingBottom: 15,
                flexShrink: 0,
                ...(isMobile && {
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "#fff",
                  borderTop: "1px solid #e0e0e0",
                  zIndex: 1200,
                }),
              }}
            >
              <CustomButtonV2
                variant="text"
                disableRipple
                btnName="Cancel"
                btnStyle={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  border: "none",
                  color: "#000000",
                  minWidth: "auto",
                  padding: 0,
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                  "&:focus": {
                    backgroundColor: "transparent",
                  },
                  "&:active": {
                    backgroundColor: "transparent",
                  },
                }}
                handleOnClick={() => navigate("/dashboard/rule-center")}
              />

              {activeStep > 0 && (
                <CustomButtonV2
                  variant="outlined"
                  btnName="Back"
                  type="cancel"
                  handleOnClick={handleBack}
                />
              )}

              <CustomButtonV2
                variant="contained"
                btnName={activeStep === 2 ? "Finish" : "Next"}
                handleOnClick={handleNext}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <ReactFlowProvider>
        {workflowHostRef.current && (
          <WorkflowPortal container={workflowHostRef.current}>
            <RuleWorkflowBuilder
              ref={workflowRef}
              ruleSetId={currentRuleSetId}
              initialWorkflow={workflowMeta}
              onFullscreen={() => setWorkflowFullscreen(!workflowFullscreen)}
              fullScreenMode={workflowFullscreen}
              initialWorkflowId={initialWorkflowId}
            />
          </WorkflowPortal>
        )}
      </ReactFlowProvider>
    </>
  );
}

export default RuleSetWorkflowPage;
